import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie, faHandshake, faUsers, faWallet, faCoins, faArrowDown,
  faChevronDown, faChevronUp, faCircle, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);

const BKK = 'Asia/Bangkok';

const DATE_PICKER_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

interface MerchantBreakdown {
  merchant_id: string;
  merchant_name: string;
  fee_percentage: number;
  payout_fee_percentage?: number;
  total_commission: number;
  payout_commission?: number;
  count: number;
}

interface ResellerSummary {
  id: string;
  name: string;
  email: string;
  company_name: string;
  fee_rate: number;
  payout_fee_rate: number;
  is_active: boolean;
  parent_reseller_id: string | null;
  parent_reseller_name: string | null;
  merchant_count: number;
  wallet_balance: number;
  total_earned: number;
  total_withdrawn: number;
  period_commission: number;
  period_transactions: number;
  period_payin_commission: number;
  period_payout_commission: number;
  period_payout_transactions: number;
  period_parent_commission: number;
  period_parent_payout_commission: number;
  merchant_breakdown: MerchantBreakdown[];
}

interface Overview {
  total_resellers: number;
  active_resellers: number;
  total_merchants: number;
  total_wallet_balance: number;
  total_earned: number;
  total_withdrawn: number;
  period_commission: number;
  period_payout_commission: number;
  period_transactions: number;
}

interface ResellerOption {
  id: string;
  name: string;
}

const defaultStartDate = () => dayjs().tz(BKK).startOf('month').toDate();
const defaultEndDate = () => dayjs().tz(BKK).endOf('day').toDate();

const fmt = (val: number) =>
  val.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ResellerReportsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [resellers, setResellers] = useState<ResellerSummary[]>([]);
  const [resellerOptions, setResellerOptions] = useState<ResellerOption[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [draftStartDate, setDraftStartDate] = useState<Date>(defaultStartDate);
  const [draftEndDate, setDraftEndDate] = useState<Date>(defaultEndDate);
  const [appliedStartDate, setAppliedStartDate] = useState<Date>(defaultStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState<Date>(defaultEndDate);

  const [draftResellerId, setDraftResellerId] = useState('');
  const [appliedResellerId, setAppliedResellerId] = useState('');

  const loadReport = useCallback(async (
    start = appliedStartDate,
    end = appliedEndDate,
    resellerId = appliedResellerId,
  ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', dayjs(start).toISOString());
      params.append('end_date', dayjs(end).toISOString());
      if (resellerId) params.append('reseller_id', resellerId);

      const response = await merchantApi.get(`/admin/resellers/reports?${params}`);
      const data = response.data.data;
      setOverview(data.overview);
      setResellers(data.resellers || []);

      if (!resellerId && data.resellers?.length) {
        setResellerOptions(data.resellers.map((r: ResellerSummary) => ({ id: r.id, name: r.name })));
      }
    } catch {
      toast.error('ไม่สามารถโหลดรายงานได้');
    } finally {
      setLoading(false);
    }
  }, [appliedStartDate, appliedEndDate, appliedResellerId]);

  useEffect(() => {
    loadReport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    if (dayjs(draftStartDate).isAfter(dayjs(draftEndDate))) {
      toast.error('วันเริ่มต้นต้องน้อยกว่าหรือเท่ากับวันสิ้นสุด');
      return;
    }
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
    setAppliedResellerId(draftResellerId);
    setExpandedId(null);
    loadReport(draftStartDate, draftEndDate, draftResellerId);
  };

  const handleReset = () => {
    const start = defaultStartDate();
    const end = defaultEndDate();
    setDraftStartDate(start);
    setDraftEndDate(end);
    setAppliedStartDate(start);
    setAppliedEndDate(end);
    setDraftResellerId('');
    setAppliedResellerId('');
    setExpandedId(null);
    loadReport(start, end, '');
  };

  const statCards: StatCardDef[] = useMemo(() => {
    if (!overview) return [];
    return [
      {
        icon: faCoins,
        iconClassName: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
        label: 'Commission รวม (ช่วงเวลา)',
        value: <span className="text-violet-600 dark:text-violet-400">฿{fmt(overview.period_commission)}</span>,
      },
      {
        icon: faCoins,
        iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
        label: 'Payout Commission',
        value: <span className="text-orange-600 dark:text-orange-400">฿{fmt(overview.period_payout_commission || 0)}</span>,
      },
      {
        icon: faArrowDown,
        iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
        label: 'Transactions',
        value: <span className="text-gray-800 dark:text-slate-100">{overview.period_transactions.toLocaleString()}</span>,
      },
      {
        icon: faWallet,
        iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        label: 'Wallet Balance (รวม)',
        value: <span className="text-emerald-600 dark:text-emerald-400">฿{fmt(overview.total_wallet_balance)}</span>,
      },
      {
        icon: faCoins,
        iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        label: 'Commission ทั้งหมด',
        value: <span className="text-amber-600 dark:text-amber-400">฿{fmt(overview.total_earned)}</span>,
      },
      {
        icon: faHandshake,
        iconClassName: 'bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
        label: 'Resellers',
        value: (
          <span className="text-gray-800 dark:text-slate-100">
            {overview.active_resellers} / {overview.total_resellers}
          </span>
        ),
      },
      {
        icon: faUsers,
        iconClassName: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
        label: 'Merchants (มี Reseller)',
        value: <span className="text-gray-800 dark:text-slate-100">{overview.total_merchants.toLocaleString()}</span>,
      },
      {
        icon: faArrowDown,
        iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
        label: 'ถอนแล้ว (รวม)',
        value: <span className="text-red-600 dark:text-red-400">฿{fmt(overview.total_withdrawn)}</span>,
      },
      {
        icon: faWallet,
        iconClassName: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
        label: 'คงเหลือ (รวม)',
        value: (
          <span className="text-teal-600 dark:text-teal-400">
            ฿{fmt(overview.total_earned - overview.total_withdrawn)}
          </span>
        ),
      },
    ];
  }, [overview]);

  const periodLabel = `${dayjs(appliedStartDate).tz(BKK).format('DD/MM/YYYY HH:mm')} – ${dayjs(appliedEndDate).tz(BKK).format('DD/MM/YYYY HH:mm')}`;

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faChartPie}
        title="Reseller Reports"
        subtitle={`รายงาน Commission ของ Reseller ทั้งระบบ • ${resellers.length.toLocaleString()} รายการ`}
      />

      <UiFilterPanel
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={() => loadReport()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      >
        <div className="flex flex-wrap items-end gap-4 w-fit">
        <div className="w-[200px] shrink-0 z-30">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">วันเริ่มต้น</label>
          <DatePicker
            selected={draftStartDate}
            onChange={(date: Date | null) => date && setDraftStartDate(date)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="dd/MM/yyyy HH:mm"
            className={DATE_PICKER_CLASS}
          />
        </div>
        <div className="w-[200px] shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">วันสิ้นสุด</label>
          <DatePicker
            selected={draftEndDate}
            onChange={(date: Date | null) => date && setDraftEndDate(date)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="dd/MM/yyyy HH:mm"
            className={DATE_PICKER_CLASS}
          />
        </div>
        <div className="w-[220px] shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Reseller</label>
          <select
            value={draftResellerId}
            onChange={(e) => setDraftResellerId(e.target.value)}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทั้งหมด</option>
            {resellerOptions.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        </div>
      </UiFilterPanel>

      {overview && <UiStatsGrid cards={statCards} loading={loading} cols={4} />}

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-bold text-gray-800 dark:text-slate-100">รายละเอียดแต่ละ Reseller</h2>
          <p className="text-xs text-gray-400 dark:text-slate-500">{periodLabel}</p>
        </div>

        {loading && resellers.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : resellers.length === 0 ? (
          <div className="p-12 text-center text-gray-400 dark:text-slate-500">ไม่พบข้อมูล Reseller</div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-slate-700">
            {resellers.map((r) => (
              <div key={r.id}>
                <div
                  role="button"
                  tabIndex={0}
                  className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-slate-700/40 cursor-pointer transition-colors"
                  onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedId(expandedId === r.id ? null : r.id);
                    }
                  }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
                          r.parent_reseller_id ? 'bg-gradient-to-br from-orange-400 to-amber-500' : ''
                        }`}
                        style={r.parent_reseller_id ? undefined : { background: 'var(--theme-primary-gradient)' }}
                      >
                        {r.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-800 dark:text-slate-100 truncate">{r.name}</p>
                          {r.parent_reseller_id && (
                            <span className="text-[10px] font-bold bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800 rounded-full px-1.5 py-0.5 flex-shrink-0">
                              Sub
                            </span>
                          )}
                          <FontAwesomeIcon
                            icon={faCircle}
                            className={`text-[6px] ${r.is_active ? 'text-emerald-500' : 'text-red-400'}`}
                          />
                          <span className="text-xs text-gray-400 dark:text-slate-500">
                            P.In: {r.fee_rate}% · P.Out: {r.payout_fee_rate || 0}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-slate-500 truncate">
                          {r.email}{r.company_name ? ` · ${r.company_name}` : ''}
                          {r.parent_reseller_name && (
                            <span className="ml-1 text-orange-500 dark:text-orange-400">↑ {r.parent_reseller_name}</span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-gray-400 dark:text-slate-500">Commission (ช่วงเวลา)</p>
                        <p className="text-base font-bold text-violet-600 dark:text-violet-400">฿{fmt(r.period_commission)}</p>
                        {(r.period_payin_commission || 0) > 0 && (r.period_payout_commission || 0) > 0 && (
                          <p className="text-[10px] text-gray-400 dark:text-slate-500">
                            P.In ฿{fmt(r.period_payin_commission)} · P.Out ฿{fmt(r.period_payout_commission)}
                          </p>
                        )}
                        {((r.period_parent_commission || 0) + (r.period_parent_payout_commission || 0)) > 0 && (
                          <p className="text-[10px] text-orange-500 dark:text-orange-400">
                            +Sub Spread ฿{fmt((r.period_parent_commission || 0) + (r.period_parent_payout_commission || 0))}
                          </p>
                        )}
                      </div>
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-gray-400 dark:text-slate-500">Transactions</p>
                        <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">{r.period_transactions}</p>
                      </div>
                      <div className="text-right hidden md:block">
                        <p className="text-xs text-gray-400 dark:text-slate-500">Wallet</p>
                        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">฿{fmt(r.wallet_balance)}</p>
                      </div>
                      <div className="text-right hidden md:block">
                        <p className="text-xs text-gray-400 dark:text-slate-500">Merchants</p>
                        <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">{r.merchant_count}</p>
                      </div>
                      <FontAwesomeIcon
                        icon={expandedId === r.id ? faChevronUp : faChevronDown}
                        className="text-gray-300 dark:text-slate-600 text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 mt-2 sm:hidden">
                    <span className="text-xs text-gray-400 dark:text-slate-500">
                      Tx: <strong className="text-gray-600 dark:text-slate-300">{r.period_transactions}</strong>
                    </span>
                    <span className="text-xs text-gray-400 dark:text-slate-500">
                      Wallet: <strong className="text-emerald-600 dark:text-emerald-400">฿{fmt(r.wallet_balance)}</strong>
                    </span>
                    <span className="text-xs text-gray-400 dark:text-slate-500">
                      Merchants: <strong className="text-gray-600 dark:text-slate-300">{r.merchant_count}</strong>
                    </span>
                  </div>
                </div>

                {expandedId === r.id && (
                  <div className="bg-gray-50 dark:bg-slate-700/30 px-6 py-4 border-t border-gray-100 dark:border-slate-700">
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[
                        { label: 'Commission ทั้งหมด', value: `฿${fmt(r.total_earned)}`, color: 'text-violet-600 dark:text-violet-400' },
                        { label: 'ถอนแล้ว', value: `฿${fmt(r.total_withdrawn)}`, color: 'text-red-500 dark:text-red-400' },
                        { label: 'คงเหลือ', value: `฿${fmt(r.wallet_balance)}`, color: 'text-emerald-600 dark:text-emerald-400' },
                      ].map((card) => (
                        <div key={card.label} className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center border border-gray-100 dark:border-slate-600">
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">{card.label}</p>
                          <p className={`text-sm font-bold ${card.color}`}>{card.value}</p>
                        </div>
                      ))}
                    </div>

                    {((r.period_parent_commission || 0) + (r.period_parent_payout_commission || 0)) > 0 && (
                      <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-lg">
                        <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 mb-2">Spread จาก Sub-Reseller (ช่วงเวลา)</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center">
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">Payin Spread</p>
                            <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">฿{fmt(r.period_parent_commission || 0)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">Payout Spread</p>
                            <p className="text-sm font-bold text-rose-500 dark:text-rose-400">฿{fmt(r.period_parent_payout_commission || 0)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">รวม Spread</p>
                            <p className="text-sm font-bold text-orange-600 dark:text-orange-400">
                              ฿{fmt((r.period_parent_commission || 0) + (r.period_parent_payout_commission || 0))}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {(r.merchant_breakdown || []).length > 0 ? (
                      <>
                        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-2">
                          Commission แยกตาม Merchant (ช่วงเวลา)
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-gray-400 dark:text-slate-500 border-b border-gray-200 dark:border-slate-600">
                                <th className="text-left py-2 font-medium">Merchant</th>
                                <th className="text-center py-2 font-medium">Fee % (P.In/P.Out)</th>
                                <th className="text-center py-2 font-medium">Commission Rate</th>
                                <th className="text-center py-2 font-medium">Tx</th>
                                <th className="text-right py-2 font-medium">Commission</th>
                                <th className="text-right py-2 font-medium">P.Out Commission</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(r.merchant_breakdown || []).map((mb) => (
                                <tr key={mb.merchant_id} className="border-b border-gray-100 dark:border-slate-700 last:border-0">
                                  <td className="py-2 font-medium text-gray-700 dark:text-slate-200">{mb.merchant_name}</td>
                                  <td className="py-2 text-center text-gray-500 dark:text-slate-400">
                                    {mb.fee_percentage}% / {mb.payout_fee_percentage || 0}%
                                  </td>
                                  <td className="py-2 text-center text-orange-500 dark:text-orange-400 font-medium">
                                    {(mb.fee_percentage - r.fee_rate).toFixed(2)}%
                                  </td>
                                  <td className="py-2 text-center text-gray-500 dark:text-slate-400">{mb.count}</td>
                                  <td className="py-2 text-right font-semibold text-violet-600 dark:text-violet-400">
                                    ฿{fmt(mb.total_commission)}
                                  </td>
                                  <td className="py-2 text-right font-semibold text-orange-500 dark:text-orange-400">
                                    {(mb.payout_commission || 0) > 0 ? `฿${fmt(mb.payout_commission!)}` : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400 dark:text-slate-500 text-center py-4">
                        ไม่มี commission จาก Merchant ในช่วงเวลานี้
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResellerReportsPage;
